import { validateInvoiceConsistency, validateDunningConsistency } from './invoiceConsistency';
import { z } from "zod";

// --- Reusable Schemas ---

export const AddressSchema = z.object({
  street: z.string().trim().min(1, "Street is required"),
  additionalInfo: z.string().trim().optional(),
  postalCode: z.string().trim().min(1, "Postal code is required"),
  city: z.string().trim().min(1, "City is required"),
  countryCode: z.string().trim().length(2, "Country code must be 2 characters (ISO 3166-1 alpha-2)"), // e.g., "DE"
});

export const BankDetailsSchema = z.object({
  bankName: z.string().trim().optional(),
  iban: z.string().trim().min(15, "IBAN is too short").max(34, "IBAN is too long"), // Basic length validation
  bic: z.string().trim().min(8, "BIC must be 8 or 11 characters").max(11, "BIC must be 8 or 11 characters").optional(),
  accountHolder: z.string().trim().optional(),
});

export const TaxIdsSchema = z.object({
  taxIdLocal: z.string().trim().optional(), // Steuernummer
  vatId: z.string().trim().regex(/^[A-Z]{2}[A-Za-z0-9+*]{2,12}$/, "Invalid VAT ID format").optional(), // EU VAT ID
  uid: z.string().trim().optional(), // Swiss UID
});

import { TaxConfigSchema, TaxationMethod, TaxPeriod, AccountingSystem } from "./schemas/TaxSchema";
import { SmtpConfigSchema, MicrosoftGraphConfigSchema } from "./schemas/EmailSchema";
import { PeppolConfigSchema } from "./schemas/PeppolSchema";
import { SystemAIConfigSchema, AIProviderSchema, AIModelSchema } from "./schemas/AISchema";
import { RunStepSchema } from "./schemas/RunStepSchema";
import { PromptTemplateSchema, PromptVersionSchema } from "./schemas/PromptSchema";
import { EInvoiceFormat } from "./schemas/EInvoiceSchema";

// --- User & Tenant Schemas (Concept 5.6) ---

export const UserRole = z.enum(["SYSTEM_ADMIN", "ADMIN", "MEMBER", "VIEWER", "ACCOUNTANT"]);
export type UserRoleType = z.infer<typeof UserRole>;

export const MAX_INVOICE_LINE_ITEMS = 500;

export const UserSchema = z.object({
  id: z.string().uuid(),
  clerkId: z.string().trim(),
  email: z.string().trim().email(),
  fullName: z.string().trim().optional(),
  role: UserRole,
  tenantId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// Forward declarations for circular dependency resolution - actual imports at bottom
const CloudflareEmailRoutingConfigSchemaLazy = z.lazy(() => z.object({
  enabled: z.boolean().default(false),
  zoneId: z.string().trim().min(1).optional(),
  accountId: z.string().trim().optional(),
  routingRuleId: z.string().trim().optional(),
  destinationAddress: z.string().email().optional(),
  customDomain: z.string().trim().optional(),
  customDomainVerified: z.boolean().default(false),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
}));

const TenantEmailConfigSchemaLazy = z.lazy(() => z.object({
  inboundEnabled: z.boolean().default(true),
  primaryInboxAddress: z.string().email(),
  customInboxAddresses: z.array(z.string().email()).default([]),
  outboundEnabled: z.boolean().default(true),
  outboundTransport: z.enum([
    "PLATFORM",
    "SMTP",
    "MICROSOFT_GRAPH_DELEGATED",
    "MICROSOFT_GRAPH_APPLICATION",
  ]).default("PLATFORM"),
  defaultFromName: z.string().trim().optional(),
  defaultFromEmail: z.string().email().optional(),
  replyToEmail: z.string().email().optional(),
  invoiceEmailTemplate: z.string().optional(),
  dunningEmailTemplate: z.string().optional(),
  reminderEmailTemplate: z.string().optional(),
  emailSignature: z.string().optional(),
  includeLogoInEmails: z.boolean().default(true),
}));

const ArchiveConfigSchemaLazy = z.lazy(() => z.object({
  enabled: z.boolean().default(true),
  retentionYears: z.number().int().min(1).max(30).default(10),
  retentionPolicy: z.enum(["GOBD", "GDPR", "CUSTOM"]).default("GOBD"),
  archiveInvoices: z.boolean().default(true),
  archiveCreditNotes: z.boolean().default(true),
  archiveDebitNotes: z.boolean().default(true),
  archiveDunningLetters: z.boolean().default(true),
  archiveEmails: z.boolean().default(true),
  archiveAttachments: z.boolean().default(true),
  archiveAuditLogs: z.boolean().default(true),
  enableChecksums: z.boolean().default(true),
  checksumAlgorithm: z.enum(["SHA-256", "SHA-512"]).default("SHA-256"),
  periodicIntegrityCheck: z.boolean().default(true),
  integrityCheckIntervalDays: z.number().int().min(1).max(365).default(30),
  lastIntegrityCheckAt: z.string().datetime().optional(),
  lastIntegrityCheckStatus: z.enum(["PASSED", "FAILED", "PENDING"]).optional(),
  storageLocation: z.enum(["R2_EU", "R2_US", "R2_ASIA"]).default("R2_EU"),
  encryptAtRest: z.boolean().default(true),
  compressArchives: z.boolean().default(true),
  legalHoldEnabled: z.boolean().default(false),
  legalHoldReason: z.string().optional(),
  legalHoldStartDate: z.string().datetime().optional(),
  legalHoldEndDate: z.string().datetime().optional(),
  allowExport: z.boolean().default(true),
  exportFormats: z.array(z.enum(["PDF", "XML", "JSON", "CSV", "DATEV"])).default(["PDF", "XML", "JSON"]),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
}));

const AuditorConfigSchemaLazy = z.lazy(() => z.object({
  enabled: z.boolean().default(false),
  auditorAccessEnabled: z.boolean().default(false),
  auditorAccessExpiry: z.string().datetime().optional(),
  accessScopes: z.array(z.enum([
    "INVOICES", "CREDIT_NOTES", "DEBIT_NOTES", "DUNNING", "AUDIT_LOGS",
    "DOCUMENTS", "PARTNERS", "FINANCIAL_REPORTS", "EMAIL_RECORDS",
  ])).default(["INVOICES", "CREDIT_NOTES", "DEBIT_NOTES", "DUNNING", "AUDIT_LOGS"]),
  accessStartDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  accessEndDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  requireMfa: z.boolean().default(true),
  allowedIpAddresses: z.array(z.string()).default([]),
  sessionTimeoutMinutes: z.number().int().min(5).max(480).default(60),
  maxConcurrentSessions: z.number().int().min(1).max(5).default(1),
  logAllAccess: z.boolean().default(true),
  notifyOnAccess: z.boolean().default(true),
  notifyEmail: z.string().email().optional(),
  allowAuditorExport: z.boolean().default(true),
  auditorExportFormats: z.array(z.enum(["PDF", "XML", "JSON", "CSV", "DATEV"])).default(["PDF"]),
  watermarkExports: z.boolean().default(true),
}));

export const TenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1, "Company name is required"),
  legalForm: z.string().trim().optional(), // GmbH, UG, etc.
  address: AddressSchema,
  taxIds: TaxIdsSchema,
  bankDetails: z.array(BankDetailsSchema).optional(),
  baseCurrency: z.string().trim().length(3).default("EUR"), // ISO 4217
  contactEmail: z.string().trim().email().optional(),
  phone: z.string().trim().optional(), // BT-42 seller contact phone
  fax: z.string().trim().optional(),
  commercialRegisterNumber: z.string().trim().optional(), // BT-30 (HRB, Handelsregister)
  legalInformation: z.string().trim().optional(), // BT-33 (Vorstand, Geschäftsführung, Grundkapital)
  boardMembers: z.array(z.string().trim()).default([]).optional(), // Specific field for AI context mapping
  emailInboxAlias: z.string().trim().email().optional(), // Full email address, e.g. "mycompany.bills@trivoa.app"
  website: z.string().trim().url().optional(),
  taxConfig: TaxConfigSchema.optional(),
  smtpConfig: SmtpConfigSchema.optional(),
  microsoftGraphConfig: MicrosoftGraphConfigSchema.nullable().optional(),
  peppolConfig: PeppolConfigSchema.optional(),
  defaultEInvoiceFormat: EInvoiceFormat.optional(),

  // Email routing configuration (Cloudflare Email Routing)
  emailRoutingConfig: CloudflareEmailRoutingConfigSchemaLazy.optional(),
  emailConfig: TenantEmailConfigSchemaLazy.optional(),

  // GoBD-conform archiving configuration
  archiveConfig: ArchiveConfigSchemaLazy.optional(),
  auditorConfig: AuditorConfigSchemaLazy.optional(),

  subscription: z.object({
    stripeCustomerId: z.string().trim().min(1).optional(),
    stripeSubscriptionId: z.string().trim().optional(),
    tier: z.enum(["FREE", "STARTUP", "SCALEUP", "ENTERPRISE"]).default("FREE"),
    status: z.enum(["active", "past_due", "canceled", "incomplete", "incomplete_expired", "trialing", "unpaid", "paused"]).default("active"),
    currentPeriodEnd: z.string().datetime().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
  }).optional(),
  dpaAccepted: z.object({
    acceptedAt: z.string().datetime(),
    ipAddress: z.string().trim(),
    userId: z.string().trim(),
    version: z.string().trim(),
    documentVersion: z.string().trim().optional(),
    documentHash: z.string().regex(/^[a-f0-9]{64}$/).optional(),
    documentTitle: z.string().trim().optional(),
    processorName: z.string().trim().optional(),
    controllerName: z.string().trim().optional(),
    signedAt: z.string().datetime().optional(),
  }).optional(),
  debugModeExpiresAt: z.string().datetime().optional(), // ISO 8601, if present and future, debug mode is active
  status: z.enum(["ACTIVE", "LOCKED", "ARCHIVED_LEGAL_HOLD"]).default("ACTIVE").optional(),
  tier: z.enum(["FREE", "STARTUP", "SCALEUP", "ENTERPRISE"]).default("FREE").optional(), // Explicit tier field for indexing/filtering
  archivedAt: z.string().datetime().optional(), // For GoBD retention
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// --- Partner Management (Concept 5.7) ---

export const PartnerSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string().trim().min(1, "Partner name is required"),
  type: z.enum(["CUSTOMER", "SUPPLIER", "BOTH"]),
  contactEmail: z.string().trim().email().optional(),
  address: AddressSchema.optional(),
  taxIds: TaxIdsSchema.optional(),
  customerNumber: z.string().trim().optional(), // Debitorennummer (for DATEV)
  supplierNumber: z.string().trim().optional(), // Kreditorennummer (for DATEV)
  networkIds: z.object({
    peppolId: z.string().trim().optional(),
    leitwegId: z.string().trim().optional(), // B2G
  }).optional(),
  eInvoiceFormatOverride: EInvoiceFormat.nullable().optional(),
  contactPerson: z.string().trim().optional(), // Ansprechpartner name
  salutationForm: z.enum(["Herr", "Frau", ""]).optional().catch(""), // Anrede for greeting
  bankDetails: z.array(BankDetailsSchema).optional(),
  paymentTerms: z.object({
    daysDue: z.number().int().nonnegative().default(30),
    discountDays: z.number().int().nonnegative().optional(),
    discountPercent: z.number().min(0).max(100).optional(),
  }).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

// --- Invoice Schema (ISDM) (Concept 2.2) ---

export const InvoiceType = z.enum(["326", "380", "381", "383", "384", "386", "389", "875", "876", "877"]); // BT-3 invoice-family type codes used by Peppol/EN16931 flows

export const TaxCategory = z.enum(["S", "Z", "E", "AE", "K", "G", "O", "L", "M"]); // UN/ECE 5305

export const PaymentMeansCode = z.string().trim(); // BT-81 (e.g. 30 = Credit Transfer)

export const PaymentMeansSchema = z.object({
  code: PaymentMeansCode.default("30"), // BT-81
  paymentId: z.string().trim().optional(), // BT-83 (Remittance Information)
  payeeFinancialAccount: BankDetailsSchema.optional(), // BT-84
});

export const AllowanceChargeSchema = z.object({
  chargeIndicator: z.boolean(), // BT-96 (true = Charge, false = Allowance)
  reason: z.string().trim().optional(), // BT-97
  amount: z.number().finite().nonnegative(), // BT-92
  taxCategory: TaxCategory.optional(), // BT-102
  taxRate: z.number().finite().optional(), // BT-103
});

export const LineItemSchema = z.object({
  id: z.string().trim(), // BT-126 Line ID
  description: z.string().trim().min(1), // BT-153
  detailDescription: z.string().trim().optional(), // BT-154
  quantity: z.number().finite().positive(), // BT-129
  unitCode: z.string().trim().default("H87"), // BT-130 UN/ECE Rec 20 (H87 = Piece)
  unitPrice: z.number().finite(), // BT-146 Net price per unit
  unitPriceBaseQuantity: z.number().finite().positive().optional(), // BT-149 Price base quantity (default 1)
  taxRate: z.number().finite().min(0).max(100), // BT-152 Percentage (e.g., 19.0)
  taxCategory: TaxCategory.default("S"), // BT-151
  lineTotalAmount: z.number().finite(), // BT-131 quantity * unitPrice (excluding tax)
  taxAmount: z.number().finite().optional(), // Printed line tax
  unitTaxAmount: z.number().finite().optional(), // Printed tax per invoiced unit
  lineGrossAmount: z.number().finite().optional(),
  sellerItemId: z.string().trim().optional(), // BT-155
  buyerItemId: z.string().trim().optional(), // BT-156
  standardItemId: z.string().trim().optional(), // BT-157
  itemClassification: z.string().trim().optional(), // BT-158 (e.g. CPV code)
  accountingAccount: z.string().trim().optional(), // BT-133 Booking account reference
  originCountry: z.string().trim().length(2).optional(), // BT-159 ISO 3166-1 alpha-2
  note: z.string().trim().optional(),
  allowancesCharges: z.array(AllowanceChargeSchema).optional(),
  period: z.object({
    startDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(),
    endDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(),
  }).optional(),
});

export const TaxBreakdownSchema = z.object({
  taxRate: z.number().finite(),
  taxCategory: TaxCategory,
  taxableAmount: z.number().finite(), // Base amount
  taxAmount: z.number().finite(), // Calculated tax
  taxExemptionReason: z.string().trim().optional(), // BT-120
  taxExemptionReasonCode: z.string().trim().optional(), // BT-121
});

// --- Process Control (XRechnung BT-23, BT-24) ---

export const ProcessControlSchema = z.object({
  businessProcessType: z.string().trim().optional(), // BT-23 e.g. "urn:fdc:peppol.eu:2017:poacc:billing:01:1.0"
  specificationIdentifier: z.string().trim().optional(), // BT-24 e.g. "urn:cen.eu:en16931:2017#compliant#urn:xoev-de:kosit:standard:xrechnung_3.0"
});

// --- Credit Note Details ---

export const CreditNoteDetailsSchema = z.object({
  originalInvoiceNumber: z.string().trim().optional(),
  originalInvoiceDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  creditReasonCode: z.enum(["RETURN", "DEFECT", "PRICE_ADJUSTMENT", "PARTIAL_RETURN", "OTHER"]).optional(),
  creditReasonText: z.string().trim().optional(),
  partialCredit: z.boolean().optional(),
});

// --- SEPA / Discount Payment Terms Details ---

export const PaymentTermsDetailsSchema = z.object({
  description: z.string().trim().optional(), // BT-20 free-text payment terms
  discountPercent: z.number().finite().min(0).max(100).optional(), // Skontoprozentsatz
  discountDays: z.number().finite().int().nonnegative().optional(), // Skontotage
  mandateReference: z.string().trim().optional(), // BT-89 SEPA Mandatsreferenz
  creditorIdentifier: z.string().trim().optional(), // BT-90 SEPA Gläubiger-ID
});

// Base Invoice Schema (before refinement)
const BaseInvoiceSchema = z.object({
  // Header
  id: z.string().uuid().optional(), // Internal ID
  invoiceNumber: z.string().trim().min(1), // BT-1
  issueDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"), // BT-2
  dueDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(), // BT-9
  deliveryDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(), // BT-72
  deliveryPeriodStart: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(), // BT-73
  deliveryPeriodEnd: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(), // BT-74
  typeCode: InvoiceType.default("380"), // BT-3
  currency: z.string().trim().length(3), // BT-5
  taxCurrency: z.string().trim().length(3).optional(), // BT-6
  language: z.string().trim().length(2).optional(), // ISO 639-1

  // XRechnung Process Control (BT-23, BT-24)
  processControl: ProcessControlSchema.optional(),

  // References
  precedingInvoiceReference: z.string().trim().optional(), // BT-25 (Required for Credit Note/Debit Note/Corrected Invoice)
  precedingInvoiceDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional(), // BT-26
  orderReference: z.string().trim().optional(), // BT-13 (Purchase Order)
  salesOrderReference: z.string().trim().optional(), // BT-14
  contractReference: z.string().trim().optional(), // BT-12
  projectReference: z.string().trim().optional(), // BT-11
  tenderReference: z.string().trim().optional(), // BT-17
  despatchAdviceReference: z.string().trim().optional(), // BT-16
  receivingAdviceReference: z.string().trim().optional(), // BT-15
  objectIdentifier: z.string().trim().optional(), // BT-18 (e.g. meter number)
  buyerAccountingReference: z.string().trim().optional(), // BT-19

  // Credit Note specific fields
  creditNoteDetails: CreditNoteDetailsSchema.optional(),

  // Parties
  seller: z.object({ // BT-27
    name: z.string().trim().min(1),
    tradeName: z.string().trim().optional(), // BT-28
    address: AddressSchema,
    taxIds: TaxIdsSchema,
    electronicAddress: z.string().trim().optional(), // BT-34 (for e-invoice routing, e.g. Peppol)
    commercialRegisterNumber: z.string().trim().optional(), // BT-30
    legalInformation: z.string().trim().optional(), // BT-33 (e.g. Vorstand, Grundkapital)
    contact: z.object({
      name: z.string().trim().optional(), // BT-41
      phone: z.string().trim().optional(), // BT-42
      email: z.string().trim().email().optional(), // BT-43
    }).optional(),
  }),
  buyer: z.object({ // BT-44
    name: z.string().trim().min(1),
    tradeName: z.string().trim().optional(), // BT-45
    address: AddressSchema,
    taxIds: TaxIdsSchema.optional(),
    buyerReference: z.string().trim().optional(), // BT-10 (Leitweg-ID for B2G)
    electronicAddress: z.string().trim().optional(), // BT-49 (Leitweg-ID or Peppol)
    commercialRegisterNumber: z.string().trim().optional(), // BT-47
    customerNumber: z.string().trim().optional(), // BT-46
    contact: z.object({
      name: z.string().trim().optional(), // BT-56
      phone: z.string().trim().optional(), // BT-57
      email: z.string().trim().email().optional(), // BT-58
    }).optional(),
  }),

  // Payee (if different from seller) — BT-59 ff.
  payee: z.object({
    name: z.string().trim().min(1), // BT-59
    taxIds: TaxIdsSchema.optional(),
    creditorId: z.string().trim().optional(), // BT-61
  }).optional(),

  // Payment Instructions
  paymentMeans: z.array(PaymentMeansSchema).optional(),
  paymentTerms: z.string().trim().optional(), // BT-20 free text
  paymentTermsDetails: PaymentTermsDetailsSchema.optional(), // Structured discount/SEPA fields

  // Document Level Allowances/Charges
  allowancesCharges: z.array(AllowanceChargeSchema).optional(),

  // Lines
  lineItems: z.array(LineItemSchema)
    .min(1, "At least one line item is required")
    .max(MAX_INVOICE_LINE_ITEMS, `At most ${MAX_INVOICE_LINE_ITEMS} line items are allowed`),

  // Printed quantity summary; unlike money, quantities may only be summed by unit.
  totalQuantity: z.number().finite().nonnegative().optional(),
  totalQuantityUnitCode: z.string().trim().min(1).optional(),

  // Totals
  lineItemsNetTotal: z.number().finite().optional(), // BT-106 sum of line item net amounts
  documentLevelAllowancesTotal: z.number().finite().optional(), // BT-107
  documentLevelChargesTotal: z.number().finite().optional(), // BT-108
  totalNetAmount: z.number().finite().nonnegative(), // BT-109 sum of line totals
  totalTaxAmount: z.number().finite().nonnegative(), // BT-110 sum of tax amounts
  totalTaxAmountInTaxCurrency: z.number().finite().nonnegative().optional(), // BT-111
  totalGrossAmount: z.number().finite().nonnegative(), // BT-112 Net + Tax
  prepaidAmount: z.number().finite().nonnegative().optional(), // BT-113
  roundingAmount: z.number().finite().optional(), // BT-114
  amountDue: z.number().finite().optional(), // BT-115 actual amount to pay
  taxBreakdown: z.array(TaxBreakdownSchema),

  notes: z.string().trim().optional(),
});

export type InvoiceConsistencyData = z.infer<typeof BaseInvoiceSchema>;

// Draft persistence validates structure, not invoice readiness. Incomplete text and
// cleared numeric controls must survive a round trip; InvoiceSchema remains the
// authority for generation and financial decisions.
const draftText = z.string().max(10000);
const draftNumber = z.number().finite().nullable().optional();
const draftAddress = z.object({ street: draftText, additionalInfo: draftText, postalCode: draftText, city: draftText, countryCode: draftText }).partial();
const draftTaxIds = z.object({ taxIdLocal: draftText, vatId: draftText, uid: draftText }).partial();
const draftContact = z.object({ name: draftText, phone: draftText, email: draftText }).partial();
const draftAllowance = AllowanceChargeSchema.partial().extend({ amount: draftNumber, taxRate: draftNumber });
const draftLine = LineItemSchema.partial().extend({
  description: draftText.optional(), quantity: draftNumber, unitPrice: draftNumber,
  unitPriceBaseQuantity: draftNumber, taxRate: draftNumber, lineTotalAmount: draftNumber,
  taxAmount: draftNumber, unitTaxAmount: draftNumber, lineGrossAmount: draftNumber,
  originCountry: draftText.optional(),
  allowancesCharges: z.array(draftAllowance).max(MAX_INVOICE_LINE_ITEMS).optional(),
  period: z.object({ startDate: draftText, endDate: draftText }).partial().optional(),
});

export const InvoiceDraftSchema = BaseInvoiceSchema.deepPartial().extend({
  invoiceNumber: draftText.optional(), issueDate: draftText.optional(), dueDate: draftText.optional(),
  deliveryDate: draftText.optional(), deliveryPeriodStart: draftText.optional(), deliveryPeriodEnd: draftText.optional(),
  precedingInvoiceDate: draftText.optional(), currency: draftText.optional(), taxCurrency: draftText.optional(), language: draftText.optional(),
  seller: BaseInvoiceSchema.shape.seller.partial().extend({ name: draftText.optional(), address: draftAddress.optional(), taxIds: draftTaxIds.optional(), contact: draftContact.optional() }).optional(),
  buyer: BaseInvoiceSchema.shape.buyer.partial().extend({ name: draftText.optional(), address: draftAddress.optional(), taxIds: draftTaxIds.optional(), contact: draftContact.optional() }).optional(),
  payee: z.object({ name: draftText, taxIds: draftTaxIds, creditorId: draftText }).partial().optional(),
  paymentMeans: z.array(PaymentMeansSchema.partial().extend({ payeeFinancialAccount: z.object({ bankName: draftText, iban: draftText, bic: draftText, accountHolder: draftText }).partial().optional() })).max(MAX_INVOICE_LINE_ITEMS).optional(),
  paymentTermsDetails: PaymentTermsDetailsSchema.partial().extend({ discountPercent: draftNumber, discountDays: draftNumber }).optional(),
  creditNoteDetails: CreditNoteDetailsSchema.extend({ originalInvoiceDate: draftText.optional() }).optional(),
  allowancesCharges: z.array(draftAllowance).max(MAX_INVOICE_LINE_ITEMS).optional(),
  lineItems: z.array(draftLine).max(MAX_INVOICE_LINE_ITEMS).optional(),
  totalQuantity: draftNumber, totalQuantityUnitCode: draftText.optional(),
  lineItemsNetTotal: draftNumber, documentLevelAllowancesTotal: draftNumber, documentLevelChargesTotal: draftNumber,
  totalNetAmount: draftNumber, totalTaxAmount: draftNumber, totalGrossAmount: draftNumber,
  totalTaxAmountInTaxCurrency: draftNumber, prepaidAmount: draftNumber, roundingAmount: draftNumber, amountDue: draftNumber,
  taxBreakdown: z.array(TaxBreakdownSchema.partial().extend({ taxRate: draftNumber, taxableAmount: draftNumber, taxAmount: draftNumber })).max(MAX_INVOICE_LINE_ITEMS).optional(),
}).superRefine((data, ctx) => {
  // Bound inherited optional reference strings as well as the explicit draft fields.
  const inspect = (value: unknown, path: (string | number)[]): void => {
    if (typeof value === 'string' && value.length > 10000) ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: 'Draft field exceeds 10000 characters' });
    if (Array.isArray(value)) value.forEach((item, index) => inspect(item, [...path, index]));
    else if (value && typeof value === 'object') Object.entries(value).forEach(([key, item]) => inspect(item, [...path, key]));
  };
  inspect(data, []);
  if (JSON.stringify(data).length > 1000000) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Draft exceeds the size limit' });
});
export type InvoiceDraft = z.infer<typeof InvoiceDraftSchema>;

// Refined Invoice Schema to enforce Credit Note and Debit Note logic
export const InvoiceSchema = BaseInvoiceSchema.superRefine((data, ctx) => {
  for (const issue of validateInvoiceConsistency(data)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message, params: { rule: issue.code } });
  }
  if (data.typeCode === "381" && !data.precedingInvoiceReference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Preceding Invoice Reference (BT-25) is required for Credit Notes (381)",
      path: ["precedingInvoiceReference"],
    });
  }
  if (data.typeCode === "383" && !data.precedingInvoiceReference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Preceding Invoice Reference (BT-25) is required for Debit Notes (383)",
      path: ["precedingInvoiceReference"],
    });
  }
  if (data.typeCode === "384" && !data.precedingInvoiceReference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Preceding Invoice Reference (BT-25) is required for Corrected Invoices (384)",
      path: ["precedingInvoiceReference"],
    });
  }
});


// --- Dunning Schema (Mahnung) (Concept 5.4 & 5.5.C) ---

export const DunningLevel = z.enum(["REMINDER", "LEVEL_1", "LEVEL_2", "LEVEL_3"]);

export const DunningFeeSchema = z.object({
  description: z.string().trim().min(1),
  amount: z.number().finite().nonnegative(), // Final payable fee, including any printed tax
  netAmount: z.number().finite().nonnegative().optional(),
  taxAmount: z.number().finite().nonnegative().optional(),
  taxRate: z.number().finite().min(0).max(100).optional(),
  // Fees are typically tax-free or have specific rules, but we treat them as line items here
  // effectively.
});

const BaseDunningSchema = z.object({
  id: z.string().uuid().optional(),
  dunningNumber: z.string().trim().min(1),
  issueDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
  dueDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD"),
  level: DunningLevel,

  // Parties
  sender: z.object({
    name: z.string().trim().min(1),
    address: AddressSchema,
    contactInfo: z.string().trim().optional(),
  }),
  recipient: z.object({
    name: z.string().trim().min(1),
    address: AddressSchema,
  }),

  // Referenced Invoices (Items being dunned)
  referencedInvoices: z.array(z.object({
    invoiceId: z.string().trim().optional(), // Internal ID if available
    invoiceNumber: z.string().trim().min(1),
    issueDate: z.string().trim(),
    dueDate: z.string().trim(), // Original due date
    totalAmount: z.number().finite().nonnegative(), // Original total
    openAmount: z.number().finite().nonnegative(), // Still pending
    currency: z.string().trim().length(3),
  })).min(1, "At least one invoice must be referenced").max(MAX_INVOICE_LINE_ITEMS),

  // Financials
  totalOpenAmount: z.number().finite().nonnegative(), // Sum of invoice open amounts
  dunningFees: z.array(DunningFeeSchema).max(MAX_INVOICE_LINE_ITEMS).optional(),
  totalDunningAmount: z.number().finite().nonnegative(), // totalOpenAmount + sum(dunningFees)
  currency: z.string().trim().length(3),

  notes: z.string().trim().optional(),
});

export type DunningConsistencyData = z.infer<typeof BaseDunningSchema>;
export const DunningSchema = BaseDunningSchema.superRefine((data, ctx) => {
  for (const issue of validateDunningConsistency(data)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: issue.path, message: issue.message, params: { rule: issue.code } });
  }
});

// --- Approval Schema (Concept 5.8) ---
import { ApprovalConfigSchema, ApprovalRuleSchema } from "./schemas/ApprovalSchema";
import { FeatureFlagSchema, TenantFeatureSchema, FeatureTypeSchema } from "./schemas/FeatureSchema";
import { TemplateSchema } from "./schemas/TemplateSchema";
import { TemplateLayoutSchema } from "./schemas/TemplateLayoutSchema";
import {
  PeppolEnvironment,
  PeppolOnboardingStatus,
  PeppolParticipantIdentifierSchema,
  PeppolProductionIneligibilityReason,
  PeppolProvider,
  PeppolProviderAssignmentSchema,
  PeppolProviderCapabilitiesSchema,
  PeppolSendOutcome,
  PeppolStatus,
  PeppolTenantStatusSchema,
  PeppolTransmissionStatus,
  SystemPeppolConfigSchema,
} from "./schemas/PeppolSchema";

// Imports from SubscriptionSchema at bottom to avoid circular dep issues with TenantSchema at top?
// Actually we inlined usage in TenantSchema, so it's safe to import here for export.
import { SubscriptionSchema, SubscriptionTier, SubscriptionStatus, FeatureLimits } from "./schemas/SubscriptionSchema";
import {
  CloudflareEmailRoutingConfigSchema,
  TenantEmailConfigSchema,
  OutboundEmailRecordSchema,
  InboundEmailRecordSchema,
  OutboundTransportSchema,
  OutboundDeliveryProviderSchema,
  OutboundMailQueueMessageSchema,
} from "./schemas/EmailRoutingSchema";
import {
  ArchiveConfigSchema,
  AuditorConfigSchema,
  AuditorAccessTokenSchema,
  ArchiveEntrySchema,
  ArchiveAccessLogSchema,
  IntegrityCheckReportSchema,
} from "./schemas/ArchiveSchema";

export { ApprovalConfigSchema, ApprovalRuleSchema, TaxConfigSchema, TaxationMethod, TaxPeriod, AccountingSystem, FeatureFlagSchema, TenantFeatureSchema, FeatureTypeSchema, SmtpConfigSchema, MicrosoftGraphConfigSchema, SubscriptionTier, SubscriptionStatus, FeatureLimits, TemplateSchema, TemplateLayoutSchema, PeppolConfigSchema, PeppolProvider, PeppolEnvironment, PeppolOnboardingStatus, PeppolSendOutcome, PeppolTransmissionStatus, PeppolProductionIneligibilityReason, PeppolParticipantIdentifierSchema, PeppolProviderAssignmentSchema, PeppolProviderCapabilitiesSchema, PeppolTenantStatusSchema, PeppolStatus, SystemPeppolConfigSchema, SystemAIConfigSchema, AIProviderSchema, AIModelSchema, RunStepSchema, PromptTemplateSchema, PromptVersionSchema, CloudflareEmailRoutingConfigSchema, TenantEmailConfigSchema, OutboundEmailRecordSchema, InboundEmailRecordSchema, OutboundTransportSchema, OutboundDeliveryProviderSchema, OutboundMailQueueMessageSchema, ArchiveConfigSchema, AuditorConfigSchema, AuditorAccessTokenSchema, ArchiveEntrySchema, ArchiveAccessLogSchema, IntegrityCheckReportSchema };

export type {
  PeppolEnvironmentType,
  PeppolOnboardingStatusType,
  PeppolParticipantIdentifier,
  PeppolProductionIneligibilityReasonType,
  PeppolProviderAssignment,
  PeppolProviderCapabilities,
  PeppolProviderType,
  PeppolSendOutcomeType,
  PeppolTenantStatus,
  PeppolTransmissionStatusType,
} from "./schemas/PeppolSchema";

// Re-export TaxConfig type from TaxSchema
export type { TaxConfig } from "./schemas/TaxSchema";
export type {
  OutboundTransport,
  OutboundDeliveryProvider,
  OutboundMailQueueMessage,
} from "./schemas/EmailRoutingSchema";

// Re-export ExtractionSchema types
export {
  RecognitionSource,
  PipelineStage,
  ProcessingMetadataSchema,
  FieldConfidenceMapSchema,
  ExtractionResultSchema,
  sf, nf, df, meetsConfidenceThreshold,
} from "./schemas/ExtractionSchema";
export type {
  RecognitionSourceType,
  PipelineStageType,
  ProcessingMetadata,
  FieldConfidenceMap,
  ExtractionResult,
  ConfidenceField,
  StringField as ConfidenceStringField,
  NumberField as ConfidenceNumberField,
  DateField as ConfidenceDateField,
  BooleanField as ConfidenceBooleanField,
} from "./schemas/ExtractionSchema";

// --- Types ---
export type Address = z.infer<typeof AddressSchema>;
export type BankDetails = z.infer<typeof BankDetailsSchema>;
export type TaxIds = z.infer<typeof TaxIdsSchema>;
export type User = z.infer<typeof UserSchema>;
export type Tenant = z.infer<typeof TenantSchema>;
export type Partner = z.infer<typeof PartnerSchema>;
export type Invoice = z.infer<typeof InvoiceSchema>;
export type PaymentMeans = z.infer<typeof PaymentMeansSchema>;
export type AllowanceCharge = z.infer<typeof AllowanceChargeSchema>;
export type LineItem = z.infer<typeof LineItemSchema>;
export type ProcessControl = z.infer<typeof ProcessControlSchema>;
export type CreditNoteDetails = z.infer<typeof CreditNoteDetailsSchema>;
export type PaymentTermsDetails = z.infer<typeof PaymentTermsDetailsSchema>;
export type Dunning = z.infer<typeof DunningSchema>;
export type DunningFee = z.infer<typeof DunningFeeSchema>;
export type ApprovalConfig = z.infer<typeof ApprovalConfigSchema>;
export type ApprovalRule = z.infer<typeof ApprovalRuleSchema>;
export type FeatureFlag = z.infer<typeof FeatureFlagSchema>;
export type TenantFeature = z.infer<typeof TenantFeatureSchema>;
export type FeatureType = z.infer<typeof FeatureTypeSchema>;
export type SmtpConfig = z.infer<typeof SmtpConfigSchema>;
export type MicrosoftGraphConfig = z.infer<typeof MicrosoftGraphConfigSchema>;
export type PeppolConfig = z.infer<typeof PeppolConfigSchema>;
export type Subscription = z.infer<typeof SubscriptionSchema>;
export type Template = z.infer<typeof TemplateSchema>;
export type TemplateLayout = z.infer<typeof TemplateLayoutSchema>;
export type SystemAIConfig = z.infer<typeof SystemAIConfigSchema>;
export type AIProvider = z.infer<typeof AIProviderSchema>;
export type AIModel = z.infer<typeof AIModelSchema>;
export type RunStep = z.infer<typeof RunStepSchema>;
export type PromptTemplate = z.infer<typeof PromptTemplateSchema>;
export type PromptVersion = z.infer<typeof PromptVersionSchema>;

// Email Routing Types
export type CloudflareEmailRoutingConfig = z.infer<typeof CloudflareEmailRoutingConfigSchema>;
export type TenantEmailConfig = z.infer<typeof TenantEmailConfigSchema>;
export type OutboundEmailRecord = z.infer<typeof OutboundEmailRecordSchema>;
export type InboundEmailRecord = z.infer<typeof InboundEmailRecordSchema>;

// Archive & Auditor Types
export type ArchiveConfig = z.infer<typeof ArchiveConfigSchema>;
export type AuditorConfig = z.infer<typeof AuditorConfigSchema>;
export type AuditorAccessToken = z.infer<typeof AuditorAccessTokenSchema>;
export type ArchiveEntry = z.infer<typeof ArchiveEntrySchema>;
export type ArchiveAccessLog = z.infer<typeof ArchiveAccessLogSchema>;
export type IntegrityCheckReport = z.infer<typeof IntegrityCheckReportSchema>;

// --- Shadow Ledger Schema (Concept 5.1) ---

export const ShadowLedgerEntryType = z.enum(["PAYABLE", "RECEIVABLE"]);
export const ShadowLedgerEntryStatus = z.enum(["ESTIMATED", "CONFIRMED", "PAID", "CANCELLED"]);

export const ShadowLedgerEntrySchema = z.object({
  id: z.string().uuid().optional(),
  tenantId: z.string().uuid(),
  sourceDocumentId: z.string().trim().optional(), // Link to actual document if exists
  type: ShadowLedgerEntryType,
  amount: z.number(), // Always positive
  currency: z.string().trim().length(3),
  dueDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD").optional(),
  status: ShadowLedgerEntryStatus,
  counterpartyName: z.string().trim().optional(),
  description: z.string().trim().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

export type ShadowLedgerEntry = z.infer<typeof ShadowLedgerEntrySchema>;

export * from "./schemas/EmailProviderSchema";
